import { useEffect, useRef, useState, useCallback } from 'react';
import { NotificationOut } from '../types/api';
import { SECURITY_CONFIG } from '../config/security';
import i18n from '../i18n';

/**
 * Translate notification messages to match user's language preference
 * Translates common English notification patterns to the current language
 */
const translateNotification = (title: string, message: string): { title: string; message: string } => {
  const currentLang = i18n.language || 'es';
  
  // Translate "Order Created Successfully!" title
  if (title === 'Order Created Successfully!' || title.toLowerCase().includes('order created successfully')) {
    title = i18n.t('notifications.orderCreatedSuccessfully', { lng: currentLang });
  }
  
  // Translate order created message pattern
  // Matches: "Your order #123 has been created." or "Your order #123 has been created. Total: $15.47"
  const orderCreatedPattern = /Your order #(\d+) has been created\.?\s*(?:Total:\s*([\$]?[\d.,]+))?/i;
  const match = message.match(orderCreatedPattern);
  if (match) {
    const orderId = match[1];
    const totalAmount = match[2] || '';
    message = i18n.t('notifications.orderCreatedMessage', { orderId, lng: currentLang });
    if (totalAmount) {
      // Add translated "Total:" label with the amount
      const totalLabel = i18n.t('notifications.total', { lng: currentLang });
      message += ` ${totalLabel}: ${totalAmount}`;
    }
  }
  
  return { title, message };
};

/**
 * WebSocket message types from backend
 */
interface NewOrderEventData {
  order_id: number;
  event_id: number;
}

interface StockUpdatedEventData {
  local_id: number;
  product_id: number;
}

interface WebSocketMessage {
  type: 'connected' | 'notification' | 'unread_count' | 'pong' | 'error' | 'new_order' | 'order_queue_changed' | 'stock_updated';
  data?: NotificationOut | NewOrderEventData | StockUpdatedEventData;
  user_id?: number;
  order_id?: number;
  message?: string;
  count?: number;
  code?: string; // Error code (e.g., 'RATE_LIMIT_EXCEEDED')
}

/**
 * WebSocket connection options
 */
interface UseNotificationWebSocketOptions {
  userId?: number;
  orderId?: number;
  enabled?: boolean;
  onNotification?: (notification: NotificationOut) => void;
  onUnreadCountChange?: (count: number) => void;
  onNewOrder?: (data: NewOrderEventData) => void;
  onStockUpdated?: (data: StockUpdatedEventData) => void;
}

/**
 * Convert HTTP/HTTPS URL to WebSocket URL (ws/wss)
 */
const getWebSocketUrl = (apiUrl: string, path: string): string => {
  const url = new URL(apiUrl);
  const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${url.host}${path}`;
};

/**
 * Hook for managing WebSocket connection to notification endpoint
 * 
 * Supports both authenticated user connections and anonymous order connections
 */
export function useNotificationWebSocket(options: UseNotificationWebSocketOptions = {}) {
  const { userId, orderId, enabled = true, onNotification, onUnreadCountChange, onNewOrder, onStockUpdated } = options;
  
  const [notifications, setNotifications] = useState<NotificationOut[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Increments on every successful (re)connection so screens can refetch
  // whatever they may have missed while the socket was down.
  const [connectionId, setConnectionId] = useState(0);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const baseReconnectDelay = 2000; // 2s, doubling up to maxReconnectDelay
  const maxReconnectDelay = 30000;
  // True only when we close the socket on purpose (logout, unmount, key change)
  const manualCloseRef = useRef(false);
  const isConnectingRef = useRef(false); // Prevent multiple simultaneous connections
  const rateLimitDelay = 30000; // 30 seconds for rate limit (per backend docs)

  /**
   * Request unread count from server
   */
  const requestUnreadCount = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'get_unread_count' }));
    }
  }, []);

  /**
   * Send ping to keep connection alive
   */
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    // Don't connect if disabled or missing required params
    if (!enabled || (!userId && !orderId)) {
      return;
    }

    // Prevent multiple simultaneous connections
    if (isConnectingRef.current) {
      if (import.meta.env.DEV) {
        console.log('⚠️ WebSocket connection already in progress, skipping...');
      }
      return;
    }

    // Don't reconnect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      if (import.meta.env.DEV) {
        console.log('✅ WebSocket already connected, skipping...');
      }
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      const previous = wsRef.current;
      wsRef.current = null; // its onclose is now stale and won't reconnect
      previous.close();
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    manualCloseRef.current = false;
    isConnectingRef.current = true;

    try {
      const apiUrl = SECURITY_CONFIG.API_URL;
      const basePath = '/notifications/ws';
      
      let wsUrl: string;
      
      if (userId) {
        // Authenticated user connection
        const token = localStorage.getItem(SECURITY_CONFIG.TOKEN_STORAGE_KEY);
        if (!token) {
          console.warn('⚠️ No token found for WebSocket connection');
          setError('Authentication token not found');
          isConnectingRef.current = false;
          return;
        }
        wsUrl = `${getWebSocketUrl(apiUrl, basePath)}?token=${encodeURIComponent(token)}`;
      } else if (orderId) {
        // Anonymous order connection
        wsUrl = `${getWebSocketUrl(apiUrl, basePath)}?order_id=${orderId}`;
      } else {
        return;
      }

      if (import.meta.env.DEV) {
        console.log('🔌 Connecting to WebSocket:', wsUrl.replace(/token=[^&]+/, 'token=***'));
      }

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (import.meta.env.DEV) {
          console.log('✅ WebSocket connected');
        }
        setConnected(true);
        setConnectionId((n) => n + 1);
        setError(null);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        
        // Clear any reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = undefined;
        }

        // Request initial unread count
        requestUnreadCount();
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'connected':
              if (import.meta.env.DEV) {
                console.log('✅ WebSocket connection confirmed', {
                  user_id: message.user_id,
                  order_id: message.order_id,
                  message: message.message
                });
              }
              break;

            case 'error':
              // Handle error messages (e.g., rate limit exceeded)
              console.error('❌ WebSocket error:', message.message);
              setError(message.message || 'WebSocket error');
              
              if (message.code === 'RATE_LIMIT_EXCEEDED') {
                console.warn('⚠️ Rate limit exceeded. Connection will close.');
                // Don't attempt to reconnect immediately - wait for onclose handler
                // The onclose handler will wait 30 seconds before reconnecting
              }
              break;

            case 'notification':
              if (message.data) {
                const notification = message.data as NotificationOut;
                setNotifications((prev) => [notification, ...prev]);
                setUnreadCount((prev) => prev + 1);
                
                // Call custom notification handler
                if (onNotification) {
                  onNotification(notification);
                }
                
                // Show browser notification if permission granted
                if ('Notification' in window && Notification.permission === 'granted') {
                  try {
                    // Translate notification title and message to user's language
                    const { title: translatedTitle, message: translatedMessage } = translateNotification(
                      notification.title,
                      notification.message
                    );
                    
                    new Notification(translatedTitle, {
                      body: translatedMessage,
                      icon: '/notification-icon.png',
                    });
                  } catch (err) {
                    // Ignore notification errors
                    if (import.meta.env.DEV) {
                      console.warn('Failed to show browser notification:', err);
                    }
                  }
                }
              }
              break;

            case 'unread_count':
              if (message.count !== undefined) {
                setUnreadCount(message.count);
                if (onUnreadCountChange) {
                  onUnreadCountChange(message.count);
                }
              }
              break;

            // An order was claimed, released, delivered or cancelled: the
            // bartender queue refreshes the same way as for a new order.
            case 'order_queue_changed':
            case 'new_order':
              if (message.data && onNewOrder) {
                onNewOrder(message.data as NewOrderEventData);
              }
              break;

            case 'stock_updated':
              if (message.data && onStockUpdated) {
                onStockUpdated(message.data as StockUpdatedEventData);
              }
              break;

            case 'pong':
              // Keepalive response - no action needed
              break;

            default:
              if (import.meta.env.DEV) {
                console.warn('Unknown WebSocket message type:', message.type);
              }
          }
        } catch (err) {
          console.error('❌ Error parsing WebSocket message:', err);
          setError('Failed to parse WebSocket message');
        }
      };

      ws.onerror = (error) => {
        if (wsRef.current !== ws) return;
        console.error('❌ WebSocket error:', error);
        setConnected(false);
        setError('WebSocket connection error');
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        // A socket we already replaced or closed on purpose must not reconnect
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (import.meta.env.DEV) {
          console.log('🔌 WebSocket disconnected', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
        }
        setConnected(false);
        isConnectingRef.current = false;

        if (manualCloseRef.current || !enabled) {
          return;
        }

        // Always come back: phones drop sockets when the screen locks, deploys
        // restart the server (clean close 1012), and a newer tab can take our
        // slot. Giving up meant the screen silently stopped updating.
        let delay: number;
        if (event.code === 1008 && event.reason === 'Rate limit exceeded') {
          console.warn('⚠️ Rate limit exceeded. Waiting 30 seconds before reconnecting...');
          delay = rateLimitDelay;
          reconnectAttemptsRef.current = 0;
        } else {
          delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current), maxReconnectDelay);
          reconnectAttemptsRef.current += 1;
        }

        if (import.meta.env.DEV) {
          console.log(`🔄 Attempting to reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
        }

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = undefined;
          connect();
        }, delay);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('❌ Error creating WebSocket connection:', err);
      setError('Failed to create WebSocket connection');
      isConnectingRef.current = false;
    }
  }, [userId, orderId, enabled, requestUnreadCount, onNotification, onUnreadCountChange, onNewOrder, onStockUpdated]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = undefined;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    
    manualCloseRef.current = true;
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      ws.close();
    }
    
    setConnected(false);
    reconnectAttemptsRef.current = 0;
    isConnectingRef.current = false;
  }, []);

  // Setup connection and ping interval
  // Track current connection key to detect changes and prevent duplicate connections
  const connectionKeyRef = useRef<string | null>(null);
  
  useEffect(() => {
    const currentKey = `${userId || 'anon'}-${orderId || 'none'}`;
    
    // Only connect if enabled and we have userId or orderId
    if (!enabled || (!userId && !orderId)) {
      // Cleanup if disabled
      if (connectionKeyRef.current) {
        disconnect();
        connectionKeyRef.current = null;
      }
      return;
    }

    // Check current WebSocket state
    const wsState = wsRef.current?.readyState;
    const isConnected = wsState === WebSocket.OPEN;
    const isConnecting = wsState === WebSocket.CONNECTING;
    const keyChanged = connectionKeyRef.current !== currentKey;

    // Only connect if:
    // 1. Connection key changed (userId/orderId changed), OR
    // 2. Not connected, not connecting, and not already attempting to connect
    const shouldConnect = keyChanged || (!isConnected && !isConnecting && !isConnectingRef.current);

    if (shouldConnect) {
      // Cleanup old connection if key changed
      if (keyChanged && connectionKeyRef.current && wsRef.current) {
        disconnect();
      }
      
      connectionKeyRef.current = currentKey;
      connect();
    }
    // Setup ping interval (clear existing first)
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    pingIntervalRef.current = setInterval(() => {
      sendPing();
    }, 30000);
  

    return () => {
      // Cleanup on unmount or when dependencies change
      // Only cleanup ping interval here, disconnect will be handled by key change check above
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = undefined;
      }
    };
  }, [userId, orderId, enabled, connect, disconnect, sendPing]);

  // Reconnect right away when the app comes back to the foreground or the
  // network returns, instead of waiting for the backoff timer.
  useEffect(() => {
    if (!enabled || (!userId && !orderId)) return;

    const reconnectIfNeeded = () => {
      if (document.visibilityState === 'hidden') return;
      const state = wsRef.current?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
      reconnectAttemptsRef.current = 0;
      isConnectingRef.current = false;
      connect();
    };

    document.addEventListener('visibilitychange', reconnectIfNeeded);
    window.addEventListener('online', reconnectIfNeeded);
    window.addEventListener('pageshow', reconnectIfNeeded);
    return () => {
      document.removeEventListener('visibilitychange', reconnectIfNeeded);
      window.removeEventListener('online', reconnectIfNeeded);
      window.removeEventListener('pageshow', reconnectIfNeeded);
    };
  }, [enabled, userId, orderId, connect]);

  // Close the socket for good when the component using the hook unmounts
  useEffect(() => () => disconnect(), [disconnect]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch((err) => {
        if (import.meta.env.DEV) {
          console.warn('Failed to request notification permission:', err);
        }
      });
    }
  }, []);

  return {
    notifications,
    unreadCount,
    connected,
    connectionId,
    error,
    requestUnreadCount,
    disconnect,
    reconnect: connect,
  };
}
